// The release-note card body — ONE renderer shared by the WhatsNewModal (what users
// see) and the admin authoring Preview panel, so "Preview renders the exact popup"
// is true by construction, not by keeping two layouts in sync. Visual target:
// whats-new-popup-mockup.html (role chips → title → summary → hero → numbered steps
// with screenshots → markdown body).

import { C, F, M } from '../ops-board/theme';
import SafeMarkdown from './SafeMarkdown';
import type { ContentAssetOut, ContentStep } from '../../lib/content';

export const ROLE_LABELS: Record<string, string> = {
  all: 'Everyone',
  pilot: 'Pilots',
  instructor: 'Instructors',
  ops_controller: 'Ops',
  haamc: 'HAAMC',
  admin: 'Admin',
  student: 'Students',
  client: 'Clients',
};

export interface ReleaseNoteCardData {
  title: string;
  summary: string | null;
  body: string | null;
  steps: ContentStep[] | null;
  hero_asset_id: string | null;
  audience_roles: string[];
}

export default function ReleaseNoteCard({ data, assets }: { data: ReleaseNoteCardData; assets: ContentAssetOut[] }) {
  const assetById = new Map(assets.map((a) => [a.id, a]));
  const hero = data.hero_asset_id ? assetById.get(data.hero_asset_id) : undefined;
  const steps = data.steps ?? [];
  return (
    <div>
      {data.audience_roles.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {data.audience_roles.map((r) => (
            <span key={r} style={{ fontSize: 11, fontWeight: 700, color: C.accent, background: C.accentLight, border: `1px solid ${C.accentBorder}`, borderRadius: 999, padding: '2px 9px' }}>
              For: {ROLE_LABELS[r] ?? r}
            </span>
          ))}
        </div>
      )}
      <div style={{ fontSize: 17, fontWeight: 800, margin: '2px 0 6px', color: C.text, fontFamily: F }}>{data.title || 'Untitled'}</div>
      {data.summary && <p style={{ fontSize: 13, color: C.textDim, lineHeight: 1.5, margin: '0 0 12px' }}>{data.summary}</p>}
      {hero?.url && (
        <img
          src={hero.url} alt={hero.alt ?? ''}
          style={{ width: '100%', borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 12, display: 'block' }}
        />
      )}
      {steps.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '4px 0 10px' }}>
          {steps.map((s, i) => {
            const shot = s.asset_id ? assetById.get(s.asset_id) : undefined;
            return (
              <div key={i} style={{ fontSize: 12, color: C.textDim, background: '#f1f5f9', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 10px' }}>
                <span style={{ color: C.accent, fontWeight: 800, fontFamily: M }}>{i + 1}.</span> {s.text}
                {shot?.url && (
                  <img src={shot.url} alt={shot.alt ?? ''} style={{ width: '100%', borderRadius: 6, border: `1px solid ${C.border}`, marginTop: 6, display: 'block' }} />
                )}
              </div>
            );
          })}
        </div>
      )}
      {data.body && <SafeMarkdown text={data.body} />}
    </div>
  );
}
